import { useApiClient } from "../apiClients";
import { OBJECTIVES_ENDPOINTS } from "./endpoints";
import type { UUID } from "../../models/domain";

export interface DocumentObjective {
  id: UUID;
  document_id: UUID;
  doc_version_id: UUID;
  ordinal: number;
  text: string;
  model_name: string;
  created_at: string;

  /** Provenance : quels passages du cours enseignent cet objectif. */
  source_batch_id: UUID;
  source_chunk_ordinals: number[];
  source_chunk_ids: UUID[];
  window_index: number;
  extraction_run_id?: UUID;
  prompt_version: string;

  /** Étape 4 : nature de l'objectif décidée à l'extraction (prompt v3). */
  kind?: string;
  /** Confiance du modèle sur `kind`, dans [0,1]. */
  kind_confidence?: number;
  /** Langue de l'extrait, jamais celle de l'interface. */
  language?: string;
  /** Une unité de faits typée existe pour cet objectif. */
  has_knowledge?: boolean;

  /**
   * Leçon du chapitre importé dont l'objectif est extrait (`lecons[i].ossature_ref`
   * du manifeste), posée par la structure ; null hors chapitre importé.
   */
  lecon_ref?: string | null;
}

export interface ObjectiveContextChunk {
  id: UUID;
  ordinal: number;
  text: string;
  token_count?: number;
  /** Distingue les passages sources de ceux ajoutés par le voisinage. */
  is_source: boolean;
}

export interface ObjectiveContextResponse {
  objective: {
    id: UUID;
    document_id: UUID;
    doc_version_id: UUID;
    ordinal: number;
    text: string;
  };
  neighborhood: number;
  source_ordinals: number[];
  chunks: ObjectiveContextChunk[];
  text: string;
  total_tokens: number;
  /** Document ré-embedé sans ré-extraction : la provenance est périmée. */
  stale: boolean;
}

export interface ObjectivesResponse {
  document_id: UUID;
  doc_version_id: UUID;
  objectives: DocumentObjective[];
  from_cache: boolean;

  /**
   * Bilan de l'upsert par provenance (étape 4 §2), à lire ENSEMBLE : le
   * premier run v3 sur un document v2 orpheline la majorité des objectifs
   * (en-têtes écartés, langue posée ⇒ provenance ET texte changent), mesuré
   * 7/8 sur le document de physique. C'est sur ces chiffres que se prend la
   * décision de rejouer le parc, donc ils doivent être à l'écran.
   */
  orphaned?: number;
  /** Objectifs qui gardent leur UUID : la progression élève survit. */
  reused?: number;
  /**
   * Corrections éditoriales (désactivations, notes) emportées par la cascade
   * des objectifs orphelins : du travail humain perdu, pas une statistique.
   */
  orphaned_overrides?: number;
  /** Signalements « exercice faux » emportés par la même cascade. */
  orphaned_reports?: number;

  /** Bilan par leçon (chapitre importé) ; absent pour un autre document. */
  lessons?: LessonsRunSummary;
}

/** Statut d'une leçon : `ready` = ≥ 2 objectifs, tous avec faits ; `weak` = ≥ 1 objectif mais
 * moins de 2 ou certains sans faits ; `empty` = aucun objectif. */
export type LessonStatus = "ready" | "weak" | "empty";

/** Une leçon dans le bilan de `POST /v1/documents/:id/objectives`. */
export interface LessonRunItem {
  lecon_ref: string;
  ordre: number;
  titre: string;
  passages: number;
  status: LessonStatus;
  objectives: number;
  objectives_with_facts: number;
  reused: number;
  created: number;
  orphaned: number;
  retried: boolean;
  /** Constat restant après la nouvelle tentative (« 1 objectif »), vide sinon. */
  issue: string;
}

/** Champ `lessons` de `POST /v1/documents/:id/objectives`. */
export interface LessonsRunSummary {
  prompt_version: string;
  language: string;
  lessons: number;
  ready: number;
  weak: number;
  empty: number;
  objectives: number;
  /** Anciens objectifs repris (même UUID) : la progression élève survit. */
  reused: number;
  reused_exact: number;
  reused_by_model: number;
  created: number;
  /** Anciens objectifs sans correspondant : leur progression élève est perdue. */
  orphaned: number;
  identity_rejected: number;
  retried: number;
  reviewed: number;
  review_changed: number;
  review_rejected?: number;
  review_failed?: number;
  /** Passages d'avant la première leçon, sans objectif. */
  preamble_passages: number;
  items: LessonRunItem[];
}

/** Un objectif d'une leçon (`GET /v1/documents/:id/lessons`). */
export interface LessonObjective {
  id: UUID;
  ordinal: number;
  text: string;
  language: string | null;
  kind: string | null;
  prompt_version: string;
  /** Une unité de faits existe (définition du statut et de « jouable »). */
  has_facts: boolean;
  /** Faits actifs. */
  facts_count: number;
}

export interface LessonReport {
  lecon_ref: string;
  ordre: number;
  titre: string;
  passages: number;
  status: LessonStatus;
  objectives: LessonObjective[];
}

export interface LessonsReportSummary {
  lessons: number;
  ready: number;
  weak: number;
  empty: number;
  objectives: number;
  objectives_with_facts: number;
  preamble_passages: number;
  /** Objectifs sans leçon du manifeste (extraction ancienne, leçon disparue). */
  unassigned: number;
}

/** `GET /v1/documents/:id/lessons` : le chapitre leçon par leçon. */
export interface LessonsReport {
  document_id: UUID;
  chapter_ref: string;
  version: number;
  language: string;
  summary: LessonsReportSummary;
  lessons: LessonReport[];
  unassigned: LessonObjective[];
}

export interface RunBulkObjectivesPayload {
  documents?: UUID[];
  /** Repeuple tout le parc éligible (après la purge de la migration 0017). */
  all?: boolean;
  /** Ignore le cache et remplace les objectifs existants. */
  force?: boolean;
}

export interface RunBulkObjectivesResponse {
  status: string;
  count: number;
  message: string;
}

export const useObjectivesApi = () => {
  const apiClient = useApiClient("embed");

  const getObjectives = (documentId: UUID) =>
    apiClient.get<ObjectivesResponse>(OBJECTIVES_ENDPOINTS.GET(documentId));

  const extractObjectives = (documentId: UUID, force = false) =>
    apiClient.post<ObjectivesResponse>(
      OBJECTIVES_ENDPOINTS.EXTRACT(documentId),
      {},
      { params: force ? { force: true } : undefined },
    );

  const getObjectiveContext = (objectiveId: UUID, neighborhood = 1) =>
    apiClient.get<ObjectiveContextResponse>(
      OBJECTIVES_ENDPOINTS.CONTEXT(objectiveId),
      { params: { neighborhood } },
    );

  /** 404 si le document n'a pas de manifeste (document non importé comme chapitre). */
  const getLessons = (documentId: UUID) =>
    apiClient.get<LessonsReport>(OBJECTIVES_ENDPOINTS.LESSONS(documentId));

  const runBulkObjectives = (payload: RunBulkObjectivesPayload) =>
    apiClient.post<RunBulkObjectivesResponse>(OBJECTIVES_ENDPOINTS.RUN_BULK, payload);

  return {
    getObjectives,
    extractObjectives,
    getObjectiveContext,
    getLessons,
    runBulkObjectives,
  };
};
